import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Database, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const CRMSyncManager = ({ reducedMotion }) => {
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState('2025-01-08 10:30 AM');
  const [syncStats, setSyncStats] = useState({
    total_records: 1247,
    successful: 1235,
    failed: 12,
    last_sync_duration: '4.2s'
  });
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/integrations/crm/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ force: true })
      });
      
      if (response.ok) {
        const data = await response.json();
        setSyncStats({
          total_records: data.total || syncStats.total_records,
          successful: data.successful || syncStats.successful,
          failed: data.failed || syncStats.failed,
          last_sync_duration: data.duration || syncStats.last_sync_duration
        });
        setLastSync(new Date().toLocaleString());
        toast.success('CRM sync completed successfully');
      } else {
        toast.error('CRM sync failed');
      }
    } catch (error) {
      console.error('Sync error:', error);
      toast.error('CRM sync failed');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Card className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 border-slate-700/50 backdrop-blur-xl" data-testid="crm-sync-manager-card">
      <CardHeader>
        <CardTitle className="text-xl text-slate-100 flex items-center gap-2">
          <Database className="w-5 h-5 text-ink-500" />
          CRM Sync Manager
        </CardTitle>
        <CardDescription className="text-slate-400 mt-1">
          Sync call data to your CRM in real-time
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Sync Status */}
          <div className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-slate-400">Last Sync</p>
                <p className="text-lg font-medium text-slate-100" data-testid="last-sync">{lastSync}</p>
              </div>
              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30" data-testid="sync-status">
                <CheckCircle className="w-3 h-3 mr-1" />
                Active
              </Badge>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-slate-400 mb-1">Total Records</p>
                <p className="text-xl font-semibold text-slate-100" data-testid="total-records">{syncStats.total_records.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 mb-1">Successful</p>
                <p className="text-xl font-semibold text-emerald-400" data-testid="successful-records">{syncStats.successful.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400 mb-1">Failed</p>
                <p className="text-xl font-semibold text-red-400" data-testid="failed-records">{syncStats.failed}</p>
              </div>
            </div>
          </div>

          {/* Sync Button */}
          <Button
            onClick={handleSync}
            disabled={syncing}
            className="w-full bg-gold hover:bg-gold-700 text-white"
            data-testid="sync-button"
          >
            {syncing ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Sync Now
              </>
            )}
          </Button>

          {/* Sync Details */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-slate-300">Data Being Synced</h4>
            <div className="space-y-2">
              {[
                { name: 'Call Outcomes', status: 'synced', count: 1247 },
                { name: 'Lead Updates', status: 'synced', count: 892 },
                { name: 'Appointment Data', status: 'synced', count: 345 },
                { name: 'Call Recordings', status: 'pending', count: 12 }
              ].map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg border border-slate-700/50"
                  style={{
                    animation: reducedMotion ? 'none' : `fadeIn 0.3s ease-out ${index * 0.05}s both`
                  }}
                  data-testid={`sync-item-${index}`}
                >
                  <div className="flex items-center gap-2">
                    {item.status === 'synced' ? (
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-amber-400" />
                    )}
                    <span className="text-sm text-slate-300" data-testid="sync-item-name">{item.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400" data-testid="sync-item-count">{item.count} records</span>
                    <Badge
                      className={item.status === 'synced' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs' : 'bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs'}
                      data-testid="sync-item-status"
                    >
                      {item.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Info */}
          <div className="p-3 bg-ink-500 rounded-lg border border-ink-300 dark:border-ink-700">
            <p className="text-xs text-slate-400">
              <strong className="text-ink-500">Auto-sync:</strong> Data is automatically synced every 15 minutes. Use "Sync Now" for immediate updates.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CRMSyncManager;